import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  Output,
  EventEmitter
} from '@angular/core';
import {
  IAdminTokenPayload,
  IAppointment,
  IConsultation,
  ICustomerUser,
  ILead,
  INote,
  IOrder
} from '@care-portals/api-interfaces';
import { AuthFacade } from '../../../../+store/auth/auth.facade';
import { NoteFacade } from '../../../../+store/note/note.facade';
import { format } from 'date-fns';
import { CommonModule } from '@angular/common';
import { NameAvatarComponent } from '../note-avatar/note-avatar.component';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { FormsModule } from '@angular/forms';
import { takeWhile } from 'rxjs';
import { NzGridModule } from 'ng-zorro-antd/grid';
import {
  NzNotificationService,
  NzNotificationServiceModule
} from 'ng-zorro-antd/notification';

@Component({
  selector: 'portals-provider-note-editor',
  templateUrl: './note-editor.component.html',
  styleUrls: ['./note-editor.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    NameAvatarComponent,
    NzInputModule,
    NzButtonModule,
    FormsModule,
    NzGridModule,
    NzNotificationServiceModule
  ]
})
export class NoteEditorComponent implements OnInit, OnDestroy {
  @Input() customerId: string;
  @Input() consultation: IConsultation;
  @Input() appointment: IAppointment;
  @Input() order: IOrder;
  user: IAdminTokenPayload;
  content = '';
  isActive = false;
  id: string; // id for the note to be updated; null if new note

  @Output() contentChange = new EventEmitter<string>();
  @Output() idChange = new EventEmitter<string>();
  constructor(
    public authFacade: AuthFacade,
    private noteFacade: NoteFacade,
    private readonly notificationService: NzNotificationService
  ) {
    this.isActive = true;
  }

  ngOnInit() {
    this.authFacade.user$.subscribe(res => (this.user = res));
    if (this.consultation && this.appointment) {
      console.error(
        'Note can only be linked to an appointment or a consultation; not both'
      );
    } else if (this.consultation) {
      this.customerId =
        (this.consultation?.customer as ICustomerUser)?._id ||
        (this.consultation.customer as string);
    } else if (this.appointment) {
      this.customerId =
        (this.appointment?.relatedToLead as ILead)?._id ||
        (this.appointment.relatedToLead as string);

      this.noteFacade.appointmentNotes$
        .pipe(takeWhile(() => this.isActive))
        .subscribe(res => {
          const notesArr = Object.values(res);

          if (notesArr.length > 0) {
            // appointment can only have one note per session
            // sort notes to get the latest one, we had a bug that allowed saving multiple notes to the same appointment
            // we pick the latest note and ignore the others.
            notesArr.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            );

            this.content = notesArr[0].content;
            this.id = notesArr[0]._id;
            this.idChange.emit(this.id);
          }
        });

      this.noteFacade.getAppointmentNote(this.appointment._id);
    } else if (this.order) {
      this.noteFacade.getOrderNote(String(this.order.id)).pipe;
    }
  }

  ngOnDestroy(): void {
    this.isActive = false;
  }

  save() {
    if (!this.content?.trim()) {
      return;
    }
    let consultType;

    if (this.appointment) {
      consultType =
        'Session(' +
        format(new Date(this.appointment.reservation), 'yyyy-MM-dd') +
        ')';
    } else if (this.consultation) {
      consultType = this.consultation.type;
    }

    if (this.appointment && this.customerId != this.getAptLeadId()) {
      this.notificationService.error(
        'Oops!',
        'Looks like there was an issue saving session note, please copy your note then refresh and try again.'
      );
      return;
    }
    const noteDTO: Partial<INote> = {
      _id: this.id,
      customer: this.customerId,
      consultation: this.consultation?._id || undefined,
      appointment: this.appointment?._id || undefined,
      content: this.content,
      consultType: consultType
    };
    this.noteFacade.upsertNote(noteDTO, true);
  }

  getAptLeadId() {
    return (
      (this.appointment?.relatedToLead as ILead)?._id ||
      (this.appointment.relatedToLead as string)
    );
  }
}
